import React, { useState } from 'react';
import { Sensor } from '../../types';
import { Dialog } from '../common/Dialog';
import { logAuditEvent, AUDIT_ACTIONS } from '../../utils/auditLogger';
import { API_CONFIG } from '../../config/api.config';
import { prepareCommonHeaders } from '../../services/baseApi';

interface SetRefCalDialogProps {
  sensor: Sensor | null;
  onClose: () => void;
  /** Current user, passed through to the audit log entry. */
  user: any;
}

async function sendRefCal(sensorId: number, rrefValue: string) {
  const response = await fetch(`${API_CONFIG.baseURL}/setref`, {
    method: 'POST',
    credentials: 'include',
    headers: prepareCommonHeaders(),
    body: JSON.stringify({ sensorId, rrefValue }),
  });
  if (!response.ok) {
    throw new Error('Failed to send reference-resistor calibration');
  }
  return await response.json();
}

interface CalcRefCalBasis {
  trueTemp: number;
  reportedTempAvg: number;
  sampleCount: number;
  stddev: number;
  currentRref: number;
  latestReadingAt: string;
}

/** Asks the backend to compute the Rref value from the sensor's own recent
 * cloud-reported temperatures while it sits at a known, stable dry-block
 * temperature -- see calcRefCal() in dataAPI's mqttController.js for the
 * math. Still just a suggestion: the returned value pre-fills the Rref
 * input but the operator can override it before submitting. */
async function calcRefCal(sensorId: number, trueTemp: number, currentRref?: string) {
  const response = await fetch(`${API_CONFIG.baseURL}/calcref`, {
    method: 'POST',
    credentials: 'include',
    headers: prepareCommonHeaders(),
    body: JSON.stringify({
      sensorId,
      trueTemp,
      ...(currentRref ? { currentRref } : {}),
    }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || 'Failed to calculate reference-resistor value');
  }
  return data as { ok: true; newRref: number; basis: CalcRefCalBasis };
}

async function clearRefCal(sensorId: number) {
  const response = await fetch(`${API_CONFIG.baseURL}/setref`, {
    method: 'POST',
    credentials: 'include',
    headers: prepareCommonHeaders(),
    body: JSON.stringify({ sensorId, action: 'CLEAR' }),
  });
  if (!response.ok) {
    throw new Error('Failed to clear reference-resistor calibration');
  }
  return await response.json();
}

/** Sends a per-sensor reference-resistor calibration value over LoRa
 * ("REFCAL:" command, handleRefCalCommand() in the sensor firmware) or
 * clears it back to the shared default. This corrects the actual per-unit
 * source of multi-degree errors found 2026-09-06 (see HyperionComms's
 * CALIBRATION_LOG.md) -- a shared reference-resistor constant does not
 * match individual units' real analog chains closely enough. The Rref
 * value can be typed in directly, or calculated from the sensor's own
 * recent cloud-reported temperatures via the Calculate button (backend
 * does the math in calcRefCal(), see dataAPI/controllers/mqttController.js)
 * -- either way the Rref field stays editable right up to Submit. */
export function SetRefCalDialog({ sensor, onClose, user }: SetRefCalDialogProps) {
  const [rrefValue, setRrefValue] = useState('');
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState<string | null>(null);
  const [calSuccess, setCalSuccess] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [clearSuccess, setClearSuccess] = useState(false);

  const [trueTemp, setTrueTemp] = useState('');
  const [currentRrefOverride, setCurrentRrefOverride] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);
  const [calcBasis, setCalcBasis] = useState<CalcRefCalBasis | null>(null);

  const reset = () => {
    setRrefValue('');
    setCalError(null);
    setCalSuccess(false);
    setClearError(null);
    setClearSuccess(false);
    setTrueTemp('');
    setCurrentRrefOverride('');
    setShowAdvanced(false);
    setCalcLoading(false);
    setCalcError(null);
    setCalcBasis(null);
  };

  const handleCalculate = async () => {
    if (!sensor) return;
    const trueTempNum = Number(trueTemp);
    if (trueTemp.trim() === '' || Number.isNaN(trueTempNum)) {
      setCalcError('Enter the dry block\'s stable, true temperature first.');
      return;
    }
    setCalcLoading(true);
    setCalcError(null);
    setCalcBasis(null);
    try {
      const result = await calcRefCal(sensor.id, trueTempNum, currentRrefOverride || undefined);
      setRrefValue(String(result.newRref));
      setCalcBasis(result.basis);
    } catch (err: any) {
      setCalcError(err.message || 'Failed to calculate');
    } finally {
      setCalcLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sensor) return;
    setCalLoading(true);
    setCalError(null);
    setCalSuccess(false);
    try {
      await sendRefCal(sensor.id, rrefValue);
      await logAuditEvent(
        AUDIT_ACTIONS.SENSOR_SETREF,
        `Sensor reference-resistor calibrated - Sensor ID: ${sensor.id}, Name: ${sensor.name}, Rref: ${rrefValue} ohm`,
        user
      );
      setCalSuccess(true);
      setTimeout(handleClose, 1200);
    } catch (err: any) {
      setCalError(err.message || 'Failed to calibrate');
    } finally {
      setCalLoading(false);
    }
  };

  const handleClear = async () => {
    if (!sensor) return;
    setClearLoading(true);
    setClearError(null);
    setClearSuccess(false);
    try {
      await clearRefCal(sensor.id);
      await logAuditEvent(
        AUDIT_ACTIONS.SENSOR_SETREF_CLEAR,
        `Sensor reference-resistor calibration cleared - Sensor ID: ${sensor.id}, Name: ${sensor.name}`,
        user
      );
      setClearSuccess(true);
      setTimeout(handleClose, 1200);
    } catch (err: any) {
      setClearError(err.message || 'Failed to clear reference-resistor calibration');
    } finally {
      setClearLoading(false);
    }
  };

  return (
    <Dialog isOpen={!!sensor} onClose={handleClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Set Reference Resistor</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          Sensor: <span className="font-medium text-gray-900 dark:text-white">{sensor?.name}</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Hold the sensor at a known, stable temperature (e.g. in the dry block), enter that true
          temperature below and hit Calculate -- it derives the Rref value from this sensor's own
          recent readings. You can still edit the Rref field by hand afterwards.
        </div>
        <div className="border rounded p-2 space-y-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-end space-x-2">
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">True temperature (°C)</label>
              <input
                type="number"
                step="any"
                className="w-full border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                value={trueTemp}
                onChange={e => setTrueTemp(e.target.value)}
                disabled={calcLoading}
              />
            </div>
            <button
              type="button"
              className="px-3 py-1 rounded bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
              onClick={handleCalculate}
              disabled={calcLoading}
            >
              {calcLoading ? 'Calculating...' : 'Calculate'}
            </button>
          </div>
          {calcError && <div className="text-red-600 dark:text-red-400 text-xs">{calcError}</div>}
          {calcBasis && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Based on {calcBasis.sampleCount} recent readings averaging {calcBasis.reportedTempAvg}°C
              (±{calcBasis.stddev}°C, latest {new Date(calcBasis.latestReadingAt).toLocaleTimeString()})
              against a starting Rref of {calcBasis.currentRref}Ω.
            </div>
          )}
          <div>
            <button
              type="button"
              className="text-xs text-blue-600 dark:text-blue-400 underline"
              onClick={() => setShowAdvanced(v => !v)}
            >
              {showAdvanced ? 'Hide' : 'This sensor was already calibrated before?'}
            </button>
            {showAdvanced && (
              <div className="mt-1">
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Currently active Rref (ohms) -- check with the bench "getref" command; leave blank to assume the shared default
                </label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  value={currentRrefOverride}
                  onChange={e => setCurrentRrefOverride(e.target.value)}
                  disabled={calcLoading}
                />
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Rref (ohms)</label>
          <input
            type="number"
            step="any"
            min="100"
            max="100000"
            className="w-full border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            value={rrefValue}
            onChange={e => setRrefValue(e.target.value)}
            required
            disabled={calLoading || clearLoading}
          />
        </div>
        {calError && <div className="text-red-600 dark:text-red-400 text-xs">{calError}</div>}
        {calSuccess && <div className="text-green-600 dark:text-green-400 text-xs">Reference-resistor calibration sent successfully!</div>}
        {clearError && <div className="text-red-600 dark:text-red-400 text-xs">{clearError}</div>}
        {clearSuccess && <div className="text-green-600 dark:text-green-400 text-xs">Reference-resistor calibration cleared successfully!</div>}
        <div className="flex justify-between space-x-2 mt-4">
          <div>
            <button
              type="button"
              className="px-3 py-1 rounded bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60"
              onClick={handleClear}
              disabled={clearLoading || calLoading}
            >
              {clearLoading ? 'Clearing...' : 'Clear Calibration'}
            </button>
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs"
              onClick={handleClose}
              disabled={calLoading || clearLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1 rounded bg-yellow-600 text-white text-xs font-semibold hover:bg-yellow-700 disabled:opacity-60"
              disabled={calLoading || clearLoading}
            >
              {calLoading ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
